import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const DiscoverSection = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('curated');

  const curatedCollections = [
    {
      id: 1,
      title: "Cozy Winter Vibes",
      description: "Warm and comforting gifts for the cold season",
      image: "https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=400&h=300&fit=crop",
      itemCount: 24,
      priceRange: "$25-150",
      tags: ["Seasonal", "Comfort", "Home"],
      color: "primary"
    },
    {
      id: 2,
      title: "Tech Enthusiast",
      description: "Latest gadgets and innovative tech solutions",
      image: "https://images.unsplash.com/photo-1518717758536-85ae29035b6d?w=400&h=300&fit=crop",
      itemCount: 18,
      priceRange: "$30-500",
      tags: ["Technology", "Innovation", "Gadgets"],
      color: "secondary"
    },
    {
      id: 3,
      title: "Mindful Living",
      description: "Wellness and self-care essentials for inner peace",
      image: "https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=400&h=300&fit=crop",
      itemCount: 32,
      priceRange: "$15-120",
      tags: ["Wellness", "Self-care", "Mindfulness"],
      color: "success"
    },
    {
      id: 4,
      title: "Creative Souls",
      description: "Art supplies and creative tools for makers",
      image: "https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=400&h=300&fit=crop",
      itemCount: 27,
      priceRange: "$20-200",
      tags: ["Art", "Creative", "DIY"],
      color: "warning"
    }
  ];

  const personalizedSuggestions = [
    {
      id: 1,
      title: "For Coffee Lovers Like Sarah",
      description: "Based on her love for specialty coffee",
      image: "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=400&h=300&fit=crop",
      confidence: 94,
      reason: "Matches her interests in coffee and morning routines",
      items: ["Premium Coffee Beans", "French Press", "Coffee Grinder"]
    },
    {
      id: 2,
      title: "Photography Gear for Mike",
      description: "Perfect for his weekend photography hobby",
      image: "https://images.unsplash.com/photo-1502920917128-1aa500764cbd?w=400&h=300&fit=crop",
      confidence: 87,
      reason: "Based on his recent camera equipment searches",
      items: ["Camera Lens", "Tripod", "Photo Editing Software"]
    },
    {
      id: 3,
      title: "Fitness Enthusiast Bundle",
      description: "For your workout-loving friends",
      image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop",
      confidence: 91,
      reason: "Popular among your fitness-focused contacts",
      items: ["Resistance Bands", "Protein Shaker", "Workout Towel"]
    }
  ];

  const tabs = [
    { id: 'curated', name: 'Curated Collections', icon: 'Sparkles' },
    { id: 'personalized', name: 'For You', icon: 'User' },
    { id: 'trending', name: 'Trending', icon: 'TrendingUp' }
  ];

  const handleCollectionClick = (collection) => {
    navigate('/gift-finder', { state: { collection } });
  };

  return (
    <section className="py-16 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-success-50 rounded-full px-4 py-2 mb-4">
            <Icon name="Compass" size={16} className="text-success" />
            <span className="text-sm font-medium text-success">Discover</span>
          </div>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-accent-800 mb-4">
            Curated Just for You
          </h2>
          <p className="text-lg text-accent-600 max-w-2xl mx-auto">
            Explore handpicked collections and personalized recommendations that match your style and relationships
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-6 py-3 rounded-full font-medium text-sm transition-all duration-300 ${
                activeTab === tab.id
                  ? 'bg-success text-white shadow-soft'
                  : 'bg-surface text-accent-600 hover:bg-success-50 hover:text-success'
              }`}
            >
              <Icon name={tab.icon} size={16} />
              <span>{tab.name}</span>
            </button>
          ))}
        </div>

        {/* Curated Collections Tab */}
        {activeTab === 'curated' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {curatedCollections.map((collection) => (
              <div
                key={collection.id}
                onClick={() => handleCollectionClick(collection)}
                className="group bg-white rounded-2xl shadow-soft hover:shadow-warm border border-accent-100 overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-1"
              >
                <div className="relative overflow-hidden">
                  <Image
                    src={collection.image}
                    alt={collection.title}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute bottom-3 left-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="flex items-center justify-between text-white text-sm">
                      <span>{collection.itemCount} items</span>
                      <span>{collection.priceRange}</span>
                    </div>
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="font-heading text-lg font-semibold text-accent-800 mb-2 group-hover:text-primary transition-colors">
                    {collection.title}
                  </h3>
                  <p className="text-accent-600 text-sm mb-4">
                    {collection.description}
                  </p>
                  
                  <div className="flex flex-wrap gap-1 mb-4">
                    {collection.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="text-xs bg-accent-100 text-accent-600 px-2 py-1 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-accent-500">
                      {collection.itemCount} gifts
                    </span>
                    <Icon name="ArrowRight" size={16} className="text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Personalized Suggestions Tab */}
        {activeTab === 'personalized' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
            {personalizedSuggestions.map((suggestion) => (
              <div
                key={suggestion.id}
                onClick={() => navigate('/gift-finder', { state: { suggestion } })}
                className="group bg-white rounded-2xl shadow-soft hover:shadow-warm border border-accent-100 overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-1"
              >
                <div className="relative overflow-hidden">
                  <Image
                    src={suggestion.image}
                    alt={suggestion.title}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-3 right-3 bg-primary text-white text-xs font-medium px-2 py-1 rounded-full">
                    {suggestion.confidence}% match
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="font-heading text-lg font-semibold text-accent-800 mb-2 group-hover:text-primary transition-colors">
                    {suggestion.title}
                  </h3>
                  <p className="text-accent-600 text-sm mb-3">
                    {suggestion.description}
                  </p>
                  
                  <div className="bg-primary-50 rounded-lg p-3 mb-4">
                    <p className="text-xs text-primary font-medium mb-1">Why this works:</p>
                    <p className="text-xs text-accent-600">{suggestion.reason}</p>
                  </div>
                  
                  <div className="space-y-1 mb-4">
                    <p className="text-xs font-medium text-accent-700">Includes:</p>
                    {suggestion.items.slice(0, 3).map((item, index) => (
                      <p key={index} className="text-xs text-accent-600">• {item}</p>
                    ))}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-1">
                      <Icon name="Sparkles" size={14} className="text-primary" />
                      <span className="text-xs text-primary font-medium">AI Curated</span>
                    </div>
                    <Icon name="ArrowRight" size={16} className="text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Trending Tab */}
        {activeTab === 'trending' && (
          <div className="text-center py-12">
            <Icon name="TrendingUp" size={64} className="text-accent-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-accent-800 mb-2">
              Trending Content Coming Soon
            </h3>
            <p className="text-accent-600 mb-6">
              We're preparing the latest trending gifts and collections for you.
            </p>
            <Button
              variant="primary"
              iconName="Bell"
              iconPosition="left"
              onClick={() => navigate('/gift-finder')}
            >
              Get Notified
            </Button>
          </div>
        )}

        {/* Explore More CTA */}
        <div className="text-center">
          <Button
            variant="outline"
            size="lg"
            iconName="Compass"
            iconPosition="right"
            onClick={() => navigate('/gift-finder')}
            className="border-accent-200 hover:border-success-300 hover:bg-success-50 transition-all duration-300"
          >
            Explore All Collections
          </Button>
        </div>
      </div>
    </section>
  );
};

export default DiscoverSection;